// Sunworker: Pricing page, Role switcher, Owner portal, Guest support
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Check, Sparkles, Rocket, Shield, Zap, TrendingUp, MessageSquare,
  Mic, Phone, Terminal, Mail, Plus, Star, ChevronRight, UserCircle2,
  LogIn, Home, CreditCard
} from "lucide-react";
import { SunworkerLogo } from "./SunworkerLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDON_ICONS = {
  message: MessageSquare, mic: Mic, phone: Phone, sparkles: Sparkles,
  terminal: Terminal, rocket: Rocket, mail: Mail, chart: TrendingUp, shield: Shield,
};

/* ======================================================
   ROLE SWITCHER (header dropdown)
   ====================================================== */
export function RoleSwitcher({ role, setRole, roles }) {
  if (!roles) return null;
  const r = roles[role] || {};
  return (
    <div className="flex items-center gap-2" data-testid="role-switcher-wrapper">
      <span className="text-[11px] text-gray-400 font-mono hidden md:inline">ROLLE</span>
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        data-testid="role-switcher"
        className="bg-transparent border border-white/10 rounded text-[11px] px-2 py-1 text-gray-200"
        style={{ padding: "2px 6px", width: "auto" }}
      >
        {Object.entries(roles).map(([k, v]) => (
          <option key={k} value={k} style={{ background: "#1D2327" }}>
            {v.label}
          </option>
        ))}
      </select>
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: r.color || "#D4AF37" }}
        title={r.description}
      />
    </div>
  );
}


/* ======================================================
   PRICING PAGE  — plans + owner plans + addons + yearly toggle
   ====================================================== */
export function PricingView() {
  const [data, setData] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [cart, setCart] = useState([]);

  useEffect(() => { axios.get(`${API}/billing/plans`).then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-eb-muted">Lade…</div>;

  const price = (p) => yearly ? p.price_eur_year : p.price_eur_month;
  const unit  = () => yearly ? "/ Jahr" : "/ Monat";

  const toggleAddon = (a) =>
    setCart((c) => c.find((x) => x.id === a.id) ? c.filter((x) => x.id !== a.id) : [...c, a]);
  const inCart = (id) => !!cart.find((c) => c.id === id);
  const total = cart.reduce((s, a) => s + (yearly ? a.yearly_eur : a.price_eur_month), 0);

  const buy = async (planId) => {
    // Simulated: trigger checkout for subscription
    alert(`Checkout gestartet für Plan: ${planId}${cart.length ? ` + ${cart.length} Add-on(s)` : ""}\nIn Produktion: Stripe Subscription Checkout.`);
  };

  return (
    <div className="space-y-6" data-testid="pricing-view">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
            <CreditCard size={22} className="text-eb-primary" /> Preise & Pakete
          </h2>
          <p className="text-eb-muted text-sm">Komplettpakete · Owner-Pläne · einzelne Agenten als Add-on · 16 % Rabatt bei Jahreszahlung</p>
        </div>
        <div className="flex items-center gap-2 eb-surface border border-white/10 rounded-full p-1">
          <button
            onClick={() => setYearly(false)}
            data-testid="pricing-monthly"
            className={`px-3 py-1 rounded-full text-xs font-semibold transition ${!yearly ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}
          >Monatlich</button>
          <button
            onClick={() => setYearly(true)}
            data-testid="pricing-yearly"
            className={`px-3 py-1 rounded-full text-xs font-semibold transition flex items-center gap-1 ${yearly ? "bg-eb-primary text-[#0B0F17]" : "text-eb-muted"}`}
          >Jährlich <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1 rounded">-16%</span></button>
        </div>
      </div>

      {/* CORE PLANS */}
      <div>
        <div className="overline mb-3">Komplettpakete (Business-Suite)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.plans.map((p) => (
            <motion.div
              key={p.id}
              whileHover={{ y: -3 }}
              className={`card relative flex flex-col ${p.highlight ? "ring-2 ring-eb-primary" : ""}`}
              data-testid={`plan-${p.id}`}
            >
              {p.highlight && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-eb-primary text-[#0B0F17] text-[10px] font-bold px-2 py-0.5 rounded">BELIEBT</span>
              )}
              <div className="overline">{p.tagline}</div>
              <div className="font-heading font-extrabold text-lg mt-1">{p.name}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-heading text-3xl font-extrabold text-eb-primary">€ {price(p)}</span>
                <span className="text-xs text-eb-muted">{unit()}</span>
              </div>
              <ul className="space-y-1.5 mt-4 flex-1">
                {p.features.map((f, i) => (
                  <li key={f} className="flex items-start gap-1.5 text-xs text-eb-fg">
                    <Check size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy(p.id)}
                data-testid={`plan-cta-${p.id}`}
                className={`btn mt-4 w-full justify-center ${p.highlight ? "btn-primary" : ""}`}
              >
                {p.cta} <ChevronRight size={12} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* OWNER PLANS */}
      <div>
        <div className="overline mb-3 text-eb-primary">Für private Vermieter (Owner-Pläne)</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.owner_plans.map((p) => (
            <motion.div
              key={p.id}
              whileHover={{ y: -3 }}
              className={`card relative flex flex-col ${p.highlight ? "ring-2 ring-amber-400" : ""}`}
              data-testid={`owner-plan-${p.id}`}
              style={{ background: p.highlight ? "linear-gradient(145deg, #1F2937, #2a1f0f)" : undefined }}
            >
              <div className="flex items-center gap-2">
                <Home size={14} className="text-amber-400" />
                <div className="overline !text-amber-400">{p.tagline}</div>
              </div>
              <div className="font-heading font-extrabold text-lg mt-1">{p.name}</div>
              <div className="flex items-baseline gap-1 mt-2">
                <span className="font-heading text-3xl font-extrabold" style={{ color: "#F97316" }}>€ {price(p)}</span>
                <span className="text-xs text-eb-muted">{unit()}</span>
              </div>
              {p.listing_fee_per_property_month > 0 && (
                <div className="text-[11px] text-eb-muted mt-1">
                  + {p.listing_fee_per_property_month} €/Monat je zusätzliches Objekt
                </div>
              )}
              <ul className="space-y-1.5 mt-4 flex-1">
                {p.features.map((f, i) => (
                  <li key={f} className="flex items-start gap-1.5 text-xs">
                    <Check size={12} className="text-amber-400 mt-0.5 flex-shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => buy(p.id)}
                data-testid={`owner-plan-cta-${p.id}`}
                className="btn btn-amber mt-4 w-full justify-center"
              >
                {p.cta} <ChevronRight size={12} />
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* AGENT ADD-ONS */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Agenten als Add-on buchen</div>
            <div className="text-xs text-eb-muted">Beliebig zu jedem Plan hinzu — jederzeit kündbar</div>
          </div>
          {cart.length > 0 && (
            <div className="text-sm">
              <span className="text-eb-muted">Add-on-Summe: </span>
              <span className="font-heading font-bold text-eb-primary">€ {total}</span>
              <span className="text-xs text-eb-muted">{unit()}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.addons.map((a) => {
            const Icon = ADDON_ICONS[a.icon] || Sparkles;
            const active = inCart(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggleAddon(a)}
                data-testid={`addon-${a.id}`}
                className={`eb-surface border rounded-lg p-3 text-left transition hover:border-eb-primary ${active ? "border-eb-primary bg-eb-primary/10" : "border-white/10"}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon size={14} className="text-eb-primary" />
                    <span className="font-semibold text-sm">{a.name}</span>
                  </div>
                  {active && <Check size={14} className="text-eb-primary" />}
                </div>
                <div className="text-[11px] text-eb-muted mb-2">{a.description}</div>
                <div className="font-mono text-sm">
                  <span className="text-eb-primary font-bold">€ {yearly ? a.yearly_eur : a.price_eur_month}</span>
                  <span className="text-[10px] text-eb-muted"> {unit()}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ROLES */}
      <div>
        <div className="overline mb-3">Rollen & Zugriff</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {Object.entries(data.roles).map(([k, r]) => (
            <div key={k} className="eb-surface border border-white/10 rounded-lg p-3" data-testid={`role-card-${k}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                <span className="font-semibold text-sm">{r.label}</span>
              </div>
              <div className="text-[11px] text-eb-muted mb-2">{r.description}</div>
              <div className="text-[11px] font-mono text-eb-primary">{r.pricing}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ======================================================
   OWNER PORTAL — list your own property
   ====================================================== */
export function OwnerPortal() {
  const [myListings, setMyListings] = useState([]);
  const [form, setForm] = useState({
    name: "", type: "apartment", location: "", bedrooms: 1, sleeps: 2, base_price_eur: 100,
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const r = await axios.get(`${API}/agents/project/deployments?limit=20`);
    setMyListings(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setLoading(true); setMsg("");
    try {
      await axios.post(`${API}/agents/project/execute`, {
        property: {
          ...form,
          country: "AT",
          cleaning_fee_eur: 50,
          amenities: ["WLAN"],
          description_de: `${form.name} — Ihr Rückzugsort in ${form.location}.`,
          description_en: `${form.name} — your retreat in ${form.location}.`,
          channels_enabled: ["easybooking", "booking_com", "airbnb"],
        },
      });
      setMsg(`✓ ${form.name} eingereicht & auf 3 Portalen gelistet (Listing-Fee wird monatlich abgebucht).`);
      setForm({ name: "", type: "apartment", location: "", bedrooms: 1, sleeps: 2, base_price_eur: 100 });
      await load();
    } catch (e) {
      setMsg("Fehler: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="owner-portal-view">
      <div>
        <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
          <Home size={22} style={{ color: "#F97316" }} /> Owner-Portal — Eigenes Objekt einstellen
        </h2>
        <p className="text-eb-muted text-sm">Reiche dein Ferienobjekt ein. Wir listen es automatisch auf easybooking + Booking.com + Airbnb. Listing-Gebühr abhängig von deinem Plan.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-5">
        <div className="card">
          <div className="overline mb-3">Neues Objekt einreichen</div>
          <div className="space-y-3">
            <div>
              <div className="overline mb-1">Name</div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Alpenblick Studio" data-testid="owner-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="overline mb-1">Typ</div>
                <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="owner-form-type">
                  <option value="apartment">Apartment</option>
                  <option value="chalet">Chalet</option>
                  <option value="villa">Villa</option>
                  <option value="hotelroom">Hotelzimmer</option>
                </select>
              </div>
              <div>
                <div className="overline mb-1">Ort</div>
                <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="z.B. Mayrhofen, Tirol" data-testid="owner-form-location" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="overline mb-1">SZ</div>
                <input type="number" value={form.bedrooms}
                  onChange={(e) => setForm({ ...form, bedrooms: parseInt(e.target.value || 1) })} />
              </div>
              <div>
                <div className="overline mb-1">Pers.</div>
                <input type="number" value={form.sleeps}
                  onChange={(e) => setForm({ ...form, sleeps: parseInt(e.target.value || 2) })} />
              </div>
              <div>
                <div className="overline mb-1">€/Nacht</div>
                <input type="number" value={form.base_price_eur}
                  onChange={(e) => setForm({ ...form, base_price_eur: parseFloat(e.target.value || 0) })} />
              </div>
            </div>
            <button
              onClick={submit} disabled={loading || !form.name || !form.location}
              data-testid="owner-form-submit"
              className="btn btn-amber w-full justify-center">
              {loading ? "Wird eingereicht…" : (<><Plus size={14} /> Objekt einreichen & publizieren</>)}
            </button>
            {msg && <div className={`text-xs ${msg.startsWith("✓") ? "text-emerald-400" : "text-rose-400"}`}>{msg}</div>}
          </div>
        </div>

        <div className="card">
          <div className="overline mb-3">Meine Listings</div>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {myListings.length === 0 && <div className="text-eb-muted text-sm">Noch keine Listings.</div>}
            {myListings.map((d) => (
              <div key={d.id} className="eb-surface border border-white/10 rounded p-3" data-testid={`owner-listing-${d.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-sm">{d.property_name}</span>
                  <span className="font-mono text-[10px] text-eb-muted">{(d.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(d.channels || []).map((c, i) => (
                    <span key={c.channel} className="channel-pill" style={{ background: "#374151", color: "#F9FAFB", fontSize: 9 }}>
                      {c.channel}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


/* ======================================================
   GUEST SUPPORT — AI-powered support chat + contact
   ====================================================== */
export function GuestSupport({ lang }) {
  const [sessionId] = useState(() => "support-" + Math.random().toString(36).slice(2, 10));
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Willkommen bei Sunworker! Ich bin dein KI-Support und helfe gern bei Fragen zu Objekten, Buchungen oder dem Owner-Portal." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/agents/chat`, {
        agent: "chat", session_id: sessionId, message: text, lang,
        context: { product: "Sunworker KI & IT Management", role: "guest" },
      });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply || "" }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="support-view">
      <div>
        <h2 className="font-heading font-bold text-2xl">Support & Beratung</h2>
        <p className="text-eb-muted text-sm">KI-Support · Anfrage an unser Team · Hilfe zum Owner-Portal</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
        <div className="card !p-0 h-[55vh] flex flex-col">
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-eb-primary text-[#0B0F17] font-medium" : "eb-surface border border-white/5"
                }`}>{m.text}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 p-3 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Frag unseren KI-Support oder beschreibe dein Anliegen…"
              disabled={busy} data-testid="support-input" />
            <button className="btn btn-primary" onClick={send} disabled={busy} data-testid="support-send">
              Senden
            </button>
          </div>
        </div>
        <div className="card">
          <div className="overline mb-3">Direkte Kontakte</div>
          <div className="space-y-3 text-sm">
            <a href="mailto:hello@sunworker.ai" className="flex items-center gap-2 hover:text-eb-primary">
              <Mail size={14} /> hello@sunworker.ai
            </a>
            <a href="tel:+4989000000" className="flex items-center gap-2 hover:text-eb-primary">
              <Phone size={14} /> +49 89 000 00 00
            </a>
            <div className="text-xs text-eb-muted mt-4">
              Mo–Fr 9–18 Uhr · Samstag 10–14 Uhr<br />
              Priority-Support in Business+ Plänen 24/5.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
